/**
 * Where the API lives, decided at runtime from the page's own hostname.
 *
 * The web app answers on more than one domain (partyresume.com → heyportal.app),
 * and each has its own `api.` twin on the same server. Baking one API host into
 * the bundle at build time meant a page on heyportal.app called
 * api.partyresume.com — it worked (CORS allows both), but every request crossed
 * brands and the old domain could never be retired. So: on a known web domain
 * the API is `https://api.<that domain>/api`; everywhere else (localhost dev,
 * the dev host, the Capacitor shells at capacitor://localhost) it is whatever
 * REACT_APP_API_URL says, as before.
 *
 * The list is an allow-list on purpose. The dev host (party-resume.retic.co)
 * does NOT follow the `api.` convention, and a native shell's hostname is
 * `localhost`, so guessing from any hostname would break both.
 */
export const KNOWN_WEB_ROOTS = ['heyportal.app', 'partyresume.com'];

export function resolveApiBase(
  envUrl = process.env.REACT_APP_API_URL,
  hostname = typeof window !== 'undefined' && window.location ? window.location.hostname : '',
) {
  const fallback = envUrl || 'http://localhost:5250/api';
  const host = String(hostname || '').toLowerCase();
  const root = KNOWN_WEB_ROOTS.find((d) => host === d || host === `www.${d}`);
  return root ? `https://api.${root}/api` : fallback;
}

/** `https://api.heyportal.app/api` — the axios baseURL and every hand-built fetch. */
export const API_BASE = resolveApiBase();

/**
 * Where big files go: Portal clips and photos, gallery video.
 *
 * api.heyportal.app sits behind Cloudflare, which refuses any request body over 100 MB with a
 * 413 of its own — no CORS headers, so the browser reports a network error and the file never
 * reaches us (a 124 MB phone clip died exactly like that). api.partyresume.com is the same
 * server answering directly through nginx (210 MB), and CORS already allows every app origin
 * there. If api.partyresume.com is ever put behind the Cloudflare proxy too, this has to move
 * to direct-to-storage uploads instead.
 */
export function resolveUploadBase(apiBase = API_BASE) {
  return apiBase.replace(/^https:\/\/api\.heyportal\.app\//, 'https://api.partyresume.com/');
}

export const UPLOAD_BASE = resolveUploadBase();

/** `https://api.heyportal.app` — for SignalR hubs and other non-/api paths. */
export const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

export default API_BASE;
